import React from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  PassiveBadge,
  formatPresentationTimestamp,
} from "./ui/data-presentation";

const ACTION_LABELS = {
  adjust_stock: "Stock Adjusted",
  create_transfer: "Transfer Created",
  submit_transfer_request: "Transfer Request Submitted",
  reject_transfer_request: "Transfer Request Rejected",
  cancel_transfer_request: "Transfer Request Cancelled",
  approve_transfer_request: "Transfer Request Approved",
  edit_approved_transfer_request: "Approved Transfer Request Edited",
  receive_transfer_request: "Transfer Request Received",
  create_hold: "Spare Part Held",
  use_hold: "Held Spare Part Used",
  cancel_hold: "Spare Part Hold Cancelled",
};

const ENTITY_LABELS = {
  item: "Item",
  transfer: "Transfer",
  transfer_request: "Transfer Request",
  hold: "Hold",
};

const DETAIL_LABELS = {
  actor_email: "Actor",
  approved_lines: "Approved Lines",
  before_approved_lines: "Previous Approved Lines",
  after: "New Value",
  before: "Previous Value",
  branch: "Branch",
  created_count: "Holds Created",
  customer: "Customer",
  customer_name: "Customer",
  destination_warehouse_id: "Destination Warehouse",
  from: "Source Warehouse",
  lines: "Line Items",
  note: "Note",
  notes: "Notes",
  part_name: "Spare Part",
  part_number: "Part Number",
  requested_lines: "Requested Lines",
  qty: "Quantity",
  quantity: "Quantity",
  reason: "Reason",
  serial_number: "Serial Number",
  service_ticket_number: "Ticket Number",
  source_warehouse_id: "Source Warehouse",
  stock_moved: "Stock Moved",
  status: "Status",
  ticket_number: "Ticket Number",
  to: "Destination Warehouse",
  warehouse_id: "Warehouse",
};

const WAREHOUSE_KEYS = new Set([
  "warehouse_id",
  "source_warehouse_id",
  "destination_warehouse_id",
]);

const present = (value) => value !== null && value !== undefined && value !== "";
const plainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const titleCase = (value) => String(value || "")
  .replaceAll("_", " ")
  .replaceAll(".", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const detailLabel = (key) => DETAIL_LABELS[key] || titleCase(key);

const hiddenIdentifierKey = (key) => {
  if (WAREHOUSE_KEYS.has(key)) return false;
  return key === "id"
    || key === "entity_id"
    || key.endsWith("_id")
    || key.endsWith("_ids");
};

const warehouseMap = (warehouses = []) => new Map(
  warehouses.map((warehouse) => [warehouse.id, warehouse.name || warehouse.code || warehouse.id])
);

const warehouseName = (value, warehouses = []) => {
  if (!present(value)) return "";
  return warehouseMap(warehouses).get(value) || String(value);
};

export function formatSparePartAuditTimestamp(value) {
  return formatPresentationTimestamp(value);
}

export function sparePartAuditActionLabel(action) {
  return ACTION_LABELS[action] || titleCase(action) || "Audit Event";
}

export function sparePartAuditEntityLabel(entityType) {
  return ENTITY_LABELS[entityType] || titleCase(entityType) || "Unknown";
}

export function sparePartAuditTicketNumber(record) {
  const details = plainObject(record?.details) ? record.details : {};
  return record?.ticket_number
    || details.ticket_number
    || details.service_ticket_number
    || "";
}

export function sparePartAuditWarehouse(record, warehouses = []) {
  const details = plainObject(record?.details) ? record.details : {};
  let warehouse = details.warehouse_id
    || details.destination_warehouse_id
    || details.to;
  if (!warehouse && plainObject(details.before) && plainObject(details.after)) {
    const changedWarehouses = [...new Set([
      ...Object.keys(details.before),
      ...Object.keys(details.after),
    ])].filter((key) => details.before[key] !== details.after[key]);
    if (changedWarehouses.length === 1) [warehouse] = changedWarehouses;
  }
  if (!warehouse && details.branch) return titleCase(details.branch);
  return warehouseName(warehouse, warehouses);
}

const displayPrimitive = (value, key, warehouses = []) => {
  if (!present(value)) return "—";
  if (WAREHOUSE_KEYS.has(key)) return warehouseName(value, warehouses) || "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (/(?:^|_)(?:at|date|timestamp)$/.test(key)) return formatSparePartAuditTimestamp(value);
  if (/(?:status|type|action|branch)$/.test(key)) return titleCase(value);
  return String(value);
};

const conciseValue = (value, key, warehouses = []) => {
  if (Array.isArray(value)) {
    const primitives = value.filter((item) => !plainObject(item) && !Array.isArray(item));
    return primitives.length === value.length
      ? primitives.map((item) => displayPrimitive(item, key, warehouses)).join(", ")
      : "";
  }
  if (plainObject(value)) return "";
  return displayPrimitive(value, key, warehouses);
};

const shortText = (value, maxLength = 140) => {
  const text = String(value || "").trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
};

const changedStockEntries = (details, warehouses) => {
  if (!plainObject(details.before) || !plainObject(details.after)) return [];
  return [...new Set([...Object.keys(details.before), ...Object.keys(details.after)])]
    .filter((key) => details.before[key] !== details.after[key])
    .map((key) => ({
      field: warehouseName(key, warehouses) || detailLabel(key),
      previous: displayPrimitive(details.before[key], key, warehouses),
      next: displayPrimitive(details.after[key], key, warehouses),
    }));
};

const structuredDisplay = (value, key, warehouses, depth = 0) => {
  if (depth > 6) return "Additional nested data";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.map((item) => structuredDisplay(item, key, warehouses, depth + 1)).join(", ");
  }
  if (plainObject(value)) {
    const entries = Object.entries(value).filter(([nestedKey]) => !hiddenIdentifierKey(nestedKey));
    if (entries.length === 0) return "—";
    return entries.map(([nestedKey, nestedValue]) => (
      `${detailLabel(nestedKey)}: ${structuredDisplay(nestedValue, nestedKey, warehouses, depth + 1)}`
    )).join(" · ");
  }
  return displayPrimitive(value, key, warehouses);
};

const firstDefined = (...values) => values.find((value) => value !== undefined);

const normalizeChangeEntries = (details, warehouses) => {
  const stockChanges = changedStockEntries(details, warehouses);
  if (stockChanges.length > 0) return stockChanges;

  if (Array.isArray(details.changes)) {
    return details.changes.flatMap((change) => {
      if (!plainObject(change)) return [];
      const key = String(change.field || "");
      if (!key || hiddenIdentifierKey(key)) return [];
      return [{
        field: change.label || detailLabel(key),
        previous: structuredDisplay(
          firstDefined(change.previous_value, change.old),
          key,
          warehouses
        ),
        next: structuredDisplay(
          firstDefined(change.new_value, change.new),
          key,
          warehouses
        ),
      }];
    });
  }

  if (plainObject(details.changes)) {
    return Object.entries(details.changes).flatMap(([key, change]) => {
      if (hiddenIdentifierKey(key) || !plainObject(change)) return [];
      return [{
        field: detailLabel(key),
        previous: structuredDisplay(
          firstDefined(change.old, change.before, change.previous),
          key,
          warehouses
        ),
        next: structuredDisplay(
          firstDefined(change.new, change.after, change.current),
          key,
          warehouses
        ),
      }];
    });
  }
  return [];
};

export function buildSparePartAuditSummary(record, warehouses = []) {
  const details = plainObject(record?.details) ? record.details : {};
  const actionLabel = sparePartAuditActionLabel(record?.action);
  const ticketNumber = sparePartAuditTicketNumber(record);
  const warehouse = sparePartAuditWarehouse(record, warehouses);
  const quantity = details.qty ?? details.quantity;
  const part = details.part_name || details.part_number;
  const lines = details.lines;

  if (record?.action === "adjust_stock") {
    const changes = changedStockEntries(details, warehouses);
    if (changes.length === 1) {
      const change = changes[0];
      return `Stock at ${change.field}: ${change.previous} → ${change.next}`;
    }
    if (changes.length > 1) return `Stock changed at ${changes.length} warehouses`;
  }
  if (record?.action === "create_hold") {
    const subject = [
      present(quantity) ? `${displayPrimitive(quantity, "quantity")} ×` : "",
      part,
    ].filter(Boolean).join(" ");
    return ["Held", subject || "spare part", warehouse ? `at ${warehouse}` : ""].filter(Boolean).join(" ");
  }
  if (record?.action === "use_hold") {
    return [
      "Used held spare part",
      details.customer ? `for ${details.customer}` : "",
      details.serial_number ? `· Serial ${details.serial_number}` : "",
      warehouse ? `· ${warehouse}` : "",
    ].filter(Boolean).join(" ");
  }
  if (record?.action === "cancel_hold") {
    return ["Cancelled spare-part hold", warehouse ? `at ${warehouse}` : ""].filter(Boolean).join(" ");
  }
  if (["create_transfer", "approve_transfer_request", "edit_approved_transfer_request", "receive_transfer_request", "cancel_transfer_request"].includes(record?.action)) {
    const route = details.from && details.to ? `${details.from} → ${details.to}` : warehouse;
    return [
      actionLabel,
      present(lines) ? `· ${displayPrimitive(lines, "lines")} line${Number(lines) === 1 ? "" : "s"}` : "",
      route ? `· ${route}` : "",
    ].filter(Boolean).join(" ");
  }
  if (record?.action === "submit_transfer_request") {
    return [
      actionLabel,
      present(lines) ? `· ${displayPrimitive(lines, "lines")} line${Number(lines) === 1 ? "" : "s"}` : "",
      warehouse ? `· ${warehouse}` : "",
    ].filter(Boolean).join(" ");
  }
  if (record?.action === "reject_transfer_request" && present(details.reason)) {
    return `${actionLabel}: ${shortText(details.reason)}`;
  }
  if (ticketNumber) return `${actionLabel} · Ticket ${ticketNumber}`;

  const fallback = Object.entries(details).find(([key, value]) => (
    !hiddenIdentifierKey(key)
    && !["before", "after", "changes", "changed_fields"].includes(key)
    && present(value)
    && Boolean(conciseValue(value, key, warehouses))
  ));
  if (fallback) {
    const [key, value] = fallback;
    return `${detailLabel(key)}: ${shortText(conciseValue(value, key, warehouses))}`;
  }
  return `${actionLabel} recorded`;
}

const flattenReadableItems = (value, key, label, warehouses, depth = 0) => {
  if (depth > 4) {
    return [{ key, label, value: "Additional legacy data" }];
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return [{ key, label, value: "—" }];
    const onlyPrimitives = value.every((item) => !plainObject(item) && !Array.isArray(item));
    if (onlyPrimitives) {
      return [{
        key,
        label,
        value: value.map((item) => displayPrimitive(item, key, warehouses)).join(", "),
      }];
    }
    return value.flatMap((item, index) => flattenReadableItems(
      item,
      `${key}-${index}`,
      `${label} ${index + 1}`,
      warehouses,
      depth + 1
    ));
  }
  if (plainObject(value)) {
    const entries = Object.entries(value).filter(([key]) => !hiddenIdentifierKey(key));
    if (entries.length === 0) return [{ key, label, value: "—" }];
    return entries.flatMap(([nestedKey, nestedValue]) => flattenReadableItems(
      nestedValue,
      `${key}-${nestedKey}`,
      `${label} · ${detailLabel(nestedKey)}`,
      warehouses,
      depth + 1
    ));
  }
  return [{ key, label, value: displayPrimitive(value, key, warehouses) }];
};

function DetailGrid({ items, testId }) {
  if (items.length === 0) return null;
  return (
    <dl className="grid gap-3 sm:grid-cols-2" data-testid={testId}>
      {items.map(({ key, label, value }) => (
        <div key={key} className="rounded-sm border border-zinc-800 bg-zinc-900/60 px-3 py-2.5">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
            {label}
          </dt>
          <dd className="mt-1 break-words text-sm text-zinc-200">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function SparePartAuditDetailDialog({
  record,
  warehouses = [],
  open,
  onOpenChange,
  badgeTone = "neutral",
}) {
  const details = plainObject(record?.details) ? record.details : {};
  const actionLabel = sparePartAuditActionLabel(record?.action);
  const entityLabel = sparePartAuditEntityLabel(record?.entity_type);
  const ticketNumber = sparePartAuditTicketNumber(record);
  const warehouse = sparePartAuditWarehouse(record, warehouses);
  const summary = record?.summary || buildSparePartAuditSummary(record, warehouses);
  const changes = normalizeChangeEntries(details, warehouses);
  const omittedFromDetails = new Set([
    "before",
    "after",
    "changes",
    "changed_fields",
    "ticket_number",
    "service_ticket_number",
  ]);
  const detailItems = Object.entries(details).flatMap(([key, value]) => {
    if (hiddenIdentifierKey(key) || omittedFromDetails.has(key)) return [];
    return flattenReadableItems(value, key, detailLabel(key), warehouses);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flowpi-dialog flowpi-dialog--inspection max-w-3xl gap-0 border-zinc-800 bg-zinc-950 p-0 text-zinc-100" data-flowpi-dialog-size="inspection" data-testid="spare-part-audit-detail-dialog">
        <DialogHeader className="border-b border-zinc-800 px-6 py-5 pr-14">
          <div className="flex flex-wrap items-center gap-3">
            <PassiveBadge tone={badgeTone}>
              {actionLabel}
            </PassiveBadge>
            <DialogTitle>{actionLabel}</DialogTitle>
          </div>
          <DialogDescription className="pt-1 text-zinc-400">{summary}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(100vh-12rem)] space-y-6 overflow-y-auto px-6 py-5">
          <section className="space-y-3" data-testid="spare-part-audit-metadata">
            <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-400">Metadata</h3>
            <DetailGrid
              items={[
                { key: "timestamp", label: "Timestamp", value: formatSparePartAuditTimestamp(record?.created_at) },
                { key: "actor", label: "Actor", value: record?.actor_email || "—" },
                { key: "action", label: "Action", value: actionLabel },
                { key: "entity_type", label: "Entity Type", value: entityLabel },
                { key: "ticket_number", label: "Ticket Number", value: ticketNumber || "—" },
                { key: "warehouse_id", label: "Branch / Warehouse", value: warehouse || "—" },
                { key: "summary", label: "Summary", value: summary },
              ]}
            />
          </section>

          {changes.length > 0 ? (
            <section className="space-y-3" data-testid="spare-part-audit-changes">
              <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-400">Changes</h3>
              <div className="overflow-x-auto rounded-sm border border-zinc-800">
                <table className="w-full min-w-[520px] border-collapse text-left text-xs">
                  <thead className="bg-zinc-900 text-zinc-400">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Field</th>
                      <th className="px-3 py-2 font-semibold">Previous Value</th>
                      <th className="px-3 py-2 font-semibold">New Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800 bg-zinc-900/60">
                    {changes.map((change, index) => (
                      <tr key={`${change.field}-${index}`}>
                        <th className="px-3 py-2.5 font-semibold text-zinc-300">{change.field}</th>
                        <td className="break-words px-3 py-2.5 text-zinc-200">{change.previous}</td>
                        <td className="break-words px-3 py-2.5 text-zinc-200">{change.next}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {detailItems.length > 0 ? (
            <section className="space-y-3" data-testid="spare-part-audit-details">
              <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-400">Details</h3>
              <DetailGrid items={detailItems} />
            </section>
          ) : null}
        </div>

        <DialogFooter className="border-t border-zinc-800 px-6 py-4">
          <DialogClose asChild>
            <Button type="button" variant="ghost" data-action-category="close" data-testid="spare-part-audit-detail-close">
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
